"use client";

import { motion } from "framer-motion";
import { Lock, Shield, Share2, KeyRound, ServerCog, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { ThemeSection, ThemeText, AuroraBlobs } from "@/components/ui/theme-utils";

const FEATURES = [
  {
    title: "Encrypted packets",
    description:
      "Files split into encrypted packets in transit. No full file is ever exposed at rest.",
    icon: Lock
  },
  {
    title: "HASH integrity",
    description:
      "SHA-256 checksums verify every byte on arrival — corruption is caught before delivery.",
    icon: Shield
  },
  {
    title: "Tight permissions",
    description:
      "Expiring links, passwords, view-only, watermark — share the exact amount, nothing more.",
    icon: Share2
  },
  {
    title: "Customer-held keys",
    description:
      "Bring your own KMS keys. Zset never sees plaintext, even at the storage layer.",
    icon: KeyRound
  },
  {
    title: "Hardened edge",
    description:
      "TLS 1.3, isolated workloads, continuous monitoring. Audited infrastructure end-to-end.",
    icon: ServerCog
  },
  {
    title: "Zero-knowledge by default",
    description:
      "Your data, your keys. We can't read it, and we can't be compelled to leak it.",
    icon: Eye
  }
];

export function Security() {
  const { isDark } = useTheme();

  return (
    <ThemeSection id="security" variant="alternate" pattern="grid">
      <AuroraBlobs variant="violet" />
      <div className="container mx-auto px-5 sm:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-20">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium mb-5",
              isDark
                ? "bg-white/[0.04] border border-white/10 text-white/80"
                : "bg-white border border-stone-200 text-stone-700"
            )}
          >
            <Shield className="h-3.5 w-3.5 text-brand-500" />
            Top-notch security
          </span>
          <h2 className="responsive-heading-2 mb-5">
            <span className="text-gradient">Zero-trust</span> by design
          </h2>
          <ThemeText variant="subtle" className="responsive-text-lg">
            Your data is the most valuable thing on the wire. Zset treats it
            that way — at every layer.
          </ThemeText>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className={cn(
                "group rounded-3xl p-6 sm:p-7 transition-all duration-300",
                isDark
                  ? "bg-white/[0.025] border border-white/[0.08] hover:border-white/15 hover:shadow-glow"
                  : "bg-white border border-stone-200/80 hover:border-stone-300/80 hover:shadow-elevated"
              )}
            >
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    "inline-flex h-11 w-11 items-center justify-center rounded-2xl flex-shrink-0",
                    "bg-gradient-to-br from-brand-700/15 to-brand-900/10",
                    "ring-1 ring-inset ring-brand-500/20",
                    isDark ? "text-brand-300" : "text-brand-600"
                  )}
                >
                  <f.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold tracking-tight mb-1.5">
                    {f.title}
                  </h3>
                  <ThemeText variant="subtle" className="text-sm leading-relaxed">
                    {f.description}
                  </ThemeText>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Quote banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5 }}
          className={cn(
            "mt-14 sm:mt-20 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden",
            isDark
              ? "bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/[0.08]"
              : "bg-gradient-to-br from-stone-50 to-white border border-stone-200/80"
          )}
        >
          <div
            className="pointer-events-none absolute -top-12 left-1/2 -translate-x-1/2 h-40 w-[40rem] rounded-full opacity-50 blur-3xl"
            style={{
              background:
                "radial-gradient(circle, rgba(167,139,250,0.4), transparent 70%)"
            }}
          />
          <blockquote className="relative max-w-2xl mx-auto">
            <p className="text-2xl sm:text-3xl font-medium tracking-tight">
              The fastest, safest way to send large files.
            </p>
            <footer className="mt-5 text-base sm:text-lg text-stone-500 leading-relaxed">
              Drop a file, share a link, and move on with your day.
            </footer>
          </blockquote>
        </motion.div>
      </div>
    </ThemeSection>
  );
}
